import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, deleteDoc } from 'firebase/firestore';

function DuplicateQuestionChecker() {
  const [questions, setQuestions] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const duplicatesPerPage = 5;
  const [pageInput, setPageInput] = useState('1');

  // Calculate pagination
  const indexOfLastDuplicate = currentPage * duplicatesPerPage;
  const indexOfFirstDuplicate = indexOfLastDuplicate - duplicatesPerPage;
  const currentDuplicates = duplicates.slice(indexOfFirstDuplicate, indexOfLastDuplicate);
  const totalPages = Math.ceil(duplicates.length / duplicatesPerPage);

  const fetchQuestions = async () => {
    try {
      const questionsCollection = collection(db, 'questions');
      const questionsSnapshot = await getDocs(questionsCollection);
      const questionsList = questionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(questionsList);
      findDuplicates(questionsList);
    } catch (error) {
      console.error("Error fetching questions:", error);
      setError("Failed to fetch questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const findDuplicates = (questionsList) => {
    const duplicatesMap = new Map();
    
    questionsList.forEach(question => {
      // Normalize the question text for comparison
      const normalizedQuestion = question.question.toLowerCase().trim();
      
      if (duplicatesMap.has(normalizedQuestion)) {
        duplicatesMap.get(normalizedQuestion).push(question);
      } else {
        duplicatesMap.set(normalizedQuestion, [question]);
      }
    });

    // Filter out non-duplicates (only keep entries with more than one question)
    const duplicatesList = Array.from(duplicatesMap.values())
      .filter(questions => questions.length > 1)
      .map(questions => ({
        text: questions[0].question,
        questions: questions
      }));

    setDuplicates(duplicatesList);
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setPageInput(pageNumber.toString());
    }
  };

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handleJumpToPage = () => {
    const pageNumber = parseInt(pageInput, 10);
    if (!isNaN(pageNumber)) {
      handlePageChange(pageNumber);
    }
  };

  const handleViewDetails = (duplicate) => {
    setSelectedDuplicate(duplicate);
    setShowModal(true);
  };

  const handleDeleteClick = (question) => {
    setQuestionToDelete(question);
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      // Delete from Firebase
      await deleteDoc(doc(db, 'questions', questionToDelete.id));

      // Update local state
      const updatedQuestions = questions.filter(q => q.id !== questionToDelete.id);
      setQuestions(updatedQuestions);
      findDuplicates(updatedQuestions);

      // If the deleted question was in the currently selected duplicate group
      if (selectedDuplicate) {
        const updatedSelectedDuplicate = {
          ...selectedDuplicate,
          questions: selectedDuplicate.questions.filter(q => q.id !== questionToDelete.id)
        };
        
        // If this was the last question in the group, close the modal
        if (updatedSelectedDuplicate.questions.length <= 1) {
          setShowModal(false);
          setSelectedDuplicate(null);
        } else {
          setSelectedDuplicate(updatedSelectedDuplicate);
        }
      }

      // Close the confirmation modal
      setShowDeleteConfirmModal(false);
      setQuestionToDelete(null);
    } catch (error) {
      console.error("Error deleting question:", error);
      setDeleteError("Failed to delete question. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmModal(false);
    setQuestionToDelete(null);
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-4">{error}</div>;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-semibold mb-4">Duplicate Questions Checker</h2>
      
      {duplicates.length === 0 ? (
        <p className="text-green-500">No duplicate questions found!</p>
      ) : (
        <>
          <div className="space-y-4">
            {currentDuplicates.map((duplicate, index) => (
              <div key={index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Duplicate Question Text:</h3>
                    <p className="text-gray-700">{duplicate.text}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Found {duplicate.questions.length} duplicates
                    </p>
                  </div>
                  <button
                    onClick={() => handleViewDetails(duplicate)}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {duplicates.length > duplicatesPerPage && (
            <div className="flex justify-center mt-6 space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                <span>Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  onChange={handlePageInputChange}
                  className="w-12 px-1 py-1 border rounded text-center"
                />
                <span>of {totalPages}</span>
                <button
                  onClick={handleJumpToPage}
                  className="px-3 py-1 border rounded bg-blue-500 text-white hover:bg-blue-600"
                >
                  Go
                </button>
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Main Modal */}
      {showModal && selectedDuplicate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold">Duplicate Question Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-4">
              <h4 className="font-medium mb-2">Question Text:</h4>
              <p className="text-gray-700">{selectedDuplicate.text}</p>
            </div>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                {deleteError}
              </div>
            )}

            <div className="space-y-4">
              {selectedDuplicate.questions.map((question, index) => (
                <div key={question.id} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">Question ID: {question.id}</p>
                      <p className="text-sm text-gray-600">
                        Subject: {question.subject} | Topic: {question.topic} | 
                        Difficulty: {question.difficulty}
                      </p>
                      <div className="mt-2">
                        <p className="text-sm font-medium">Options:</p>
                        <ul className="list-disc list-inside text-sm text-gray-600">
                          {question.options.map((option, optIndex) => (
                            <li key={optIndex} className={optIndex === question.correctOption ? 'text-green-600' : ''}>
                              {option} {optIndex === question.correctOption ? '(Correct)' : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => window.location.href = `/admin/questions/edit/${question.id}`}
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(question)}
                        disabled={deleteLoading}
                        className={`text-red-500 hover:text-red-700 text-sm ${deleteLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && questionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this question? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className={`px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 ${
                  deleteLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DuplicateQuestionChecker; 